import { ChangeDetectorRef, Directive, OnChanges, OnDestroy, SimpleChanges, Type, forwardRef, inject, output, input, signal, linkedSignal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { buildTimezoneOptions } from '@app/_helpers/timezone-options';
import { isHelpPosition, resolveHelpText, SbHelpPosition } from '../sb-field-help';

export type { SbHelpPosition } from '../sb-field-help';
export type SbSelectMode = 'default' | 'timezone' | 'language';

export interface SbSelectItem {
  id?: string | number;
  name?: string;
  label?: string;
  value?: string | number;
  description?: string;
  [key: string]: unknown;
}

export const LANGUAGE_OPTIONS: SbSelectItem[] = [
  { id: 'en', name: 'English' },
  { id: 'nb', name: 'Norwegian Bokmål' },
  { id: 'sv', name: 'Swedish' },
  { id: 'da', name: 'Danish' },
  { id: 'de', name: 'German' },
  { id: 'fr', name: 'French' },
  { id: 'es', name: 'Spanish' },
];

let nextId = 0;

export function sbSelectProviders(component: () => Type<unknown>) {
  return [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(component),
      multi: true,
    },
  ];
}

@Directive()
export class SbSelectBase implements ControlValueAccessor, OnChanges, OnDestroy {
  protected readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly label = input('');
  readonly description = input('');
  /** @deprecated Prefer `help`. Still used as fallback when `help` is empty. */
  readonly info = input('');
  /** Help popover text. When set, shows a help icon per `helpPosition`. */
  readonly help = input('');
  /** Where to place the help icon when `help` (or legacy `info`) has a value. */
  readonly helpPosition = input<SbHelpPosition>('afterLabel');
  /** Hint shown only when the floating label is raised. Empty = label acts as placeholder. */
  readonly placeholder = input('');
  /** The control's accessible name when no visible label names it (e.g. a table cell). */
  readonly ariaLabel = input('');
  readonly required = input(false);
  readonly disabledInput = input(false, {alias: 'disabled'});
  readonly disabled = linkedSignal(() => this.disabledInput());
  readonly clearable = input(true);
  readonly multiple = input(false);
  readonly autocomplete = input(true);
  /**
   * Lets the user type a value that is not in `items` (ng-select's addTag): the trimmed text
   * becomes the value and an item of its own. Needs `autocomplete` (a search box to type in).
   */
  readonly addTag = input(false);
  /** The text before the typed value in the list, when `addTag` is on. */
  readonly addTagText = input('Add item');
  readonly error = input('');
  readonly id = input(`sb-select-${++nextId}`);
  readonly items = input<SbSelectItem[]>([]);
  readonly labelKey = input('name');
  readonly valueKey = input('id');
  readonly descriptionKey = input('');
  /** The item's picture, shown before its label in the list and in the field: a key or a dotted path. */
  readonly imageKey = input('');
  readonly modeInput = input<SbSelectMode>('default', {alias: 'mode'});
  readonly mode = linkedSignal(() => this.modeInput());
  /**
   * Backend type-ahead. When set, `items` is ignored and the list comes from this URL one
   * page at a time: a fixed first page when the control initialises, the next page when
   * the user scrolls to the end of the list, and a fresh first page filtered by
   * `searchParam` as they type. The response is the API's usual shape - `{data: {list,
   * paging}}` (or a bare array / `{data: [...]}`, which then has no further pages).
   */
  readonly searchEndpoint = input('');
  readonly searchParam = input('q');
  /** Page size, and the query parameters the endpoint pages by. */
  readonly pageSize = input(20);
  readonly pageParam = input('page');
  readonly pageSizeParam = input('perPage');
  /** Extra, fixed query parameters for the endpoint; a change resets the list to page one. */
  readonly searchParams = input<Record<string, string | number | boolean | null | undefined>>({});
  /**
   * The item the current value stands for, when it may not be on the loaded page - so a
   * chosen option keeps its label after the list is searched or reloaded. Set by the
   * control itself whenever the user picks something; parents only need it for values
   * they restore from elsewhere (a saved filter, a route).
   */
  readonly selectedItemInput = input<SbSelectItem | null>(null, {alias: 'selectedItem'});
  readonly selectedItem = linkedSignal(() => this.selectedItemInput());
  readonly defaultValue = input<string | number | Array<string | number> | null>(null);
  readonly bindLabel = input<string | null>(null);
  readonly bindValue = input<string | null>(null);

  // eslint-disable-next-line @angular-eslint/no-output-native -- renaming the output would change every binding to it
  readonly search = output<string>();
  readonly scrollToEnd = output<void>();
  readonly selectionChange = output<unknown>();

  readonly resolvedItems = signal<SbSelectItem[]>([]);
  value: unknown = null;
  readonly loading = signal(false);
  opened = false;
  focused = false;

  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private defaultApplied = false;
  // Backend type-ahead state: what the loaded list is a page of.
  private remoteTerm = '';
  private remotePage = 0;
  private remoteHasMore = false;
  private remoteRequest = 0;

  constructor() {
    this.search$.pipe(debounceTime(250), takeUntil(this.destroy$)).subscribe((term) => {
      this.search.emit(term);
      if (this.isRemote) {
        this.fetchRemote(term, 1);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items || changes.modeInput) {
      this.resolveItems();
    }
    // A change of endpoint or of the fixed parameters (the organization a client list is
    // narrowed by, say) means the loaded page is about something else now.
    if ((changes.searchEndpoint || changes.searchParams) && !changes.searchEndpoint?.firstChange
      && this.isRemote) {
      this.fetchRemote(this.remoteTerm, 1);
    }
    if (changes.selectedItemInput) {
      this.keepSelectedVisible();
    }
    if ((changes.defaultValue || changes.items || changes.modeInput) && !this.defaultApplied) {
      this.applyDefault();
    }
  }

  get isRemote(): boolean {
    return !!this.searchEndpoint() && this.mode() === 'default';
  }

  /**
   * ng-select filters the list by the typed term itself; with a backend type-ahead the
   * server already did that (and may have matched on more than the label), so every item
   * it returned is shown.
   */
  readonly passThroughSearch = (): boolean => true;

  get itemSearchFn(): ((term: string, item: SbSelectItem) => boolean) | null {
    return this.isRemote ? this.passThroughSearch : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get itemLabelKey(): string {
    return this.bindLabel() || this.labelKey();
  }

  get itemValueKey(): string {
    return this.bindValue() || this.valueKey();
  }

  itemLabel(item: SbSelectItem | null): string {
    const label = item?.[this.itemLabelKey];
    return label == null ? '' : String(label);
  }

  /** The picture at imageKey, or null when there is none. */
  itemImage(item: SbSelectItem | null): string | null {
    const path = this.imageKey();
    if (!path || !item) return null;
    const image = path.split('.').reduce<unknown>((value, key) => (value as Record<string, unknown>)?.[key], item);
    return typeof image === 'string' && image ? image : null;
  }

  /** What ng-select's addTag gets: false, or a typed value made into an item. */
  get tagFactory(): false | ((term: string) => SbSelectItem) {
    return this.addTag() ? this.createTag : false;
  }

  private readonly createTag = (term: string): SbSelectItem => {
    const value = (term || '').trim();
    return { [this.itemValueKey]: value, [this.itemLabelKey]: value } as SbSelectItem;
  };

  get hasValue(): boolean {
    if (this.multiple()) {
      return Array.isArray(this.value) && this.value.length > 0;
    }
    return this.value != null && this.value !== '';
  }

  get isActive(): boolean {
    return this.opened || this.focused;
  }

  /**
   * False when nothing would render in the label slot. The floating-label
   * treatment reserves top margin for a label that rises out of the control, so
   * keeping it without a label leaves an empty gap above the field.
   */
  get hasFloatingLabel(): boolean {
    return !!(this.label() || this.required() || this.showHelpAfterLabel);
  }

  /** Spacer when empty so ng-select keeps layout; real hint only when label is raised. */
  get selectPlaceholder(): string {
    // No label to act as the placeholder, so the placeholder has to carry itself.
    if (!this.hasFloatingLabel) {
      return this.placeholder() || ' ';
    }
    if (!this.hasValue && !this.isActive) {
      return ' ';
    }
    return this.placeholder() || ' ';
  }

  get helpText(): string {
    return resolveHelpText(this.help(), this.info());
  }
  get showHelpAfterLabel(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'afterLabel');
  }
  get showHelpEndInside(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'inputEndInside');
  }
  get showHelpEndOutside(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'inputEndOutside');
  }

  /** The forms API calls this and `setDisabledState` from outside the template (a `patchValue` in a subscribe). */
  writeValue(value: unknown): void {
    this.value = value;
    this.cdr.markForCheck();
    if (this.mode() === 'timezone') {
      this.resolveItems();
    }
    if (value != null && value !== '') {
      this.defaultApplied = true;
    }
    this.keepSelectedVisible();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    this.cdr.markForCheck();
  }

  onSelected(event: unknown): void {
    this.value = event == null && this.multiple() ? [] : event;
    if (this.isRemote && !this.multiple()) {
      // Remember the picked row, so its label survives the list being searched or paged
      // away from underneath it.
      this.selectedItem.set(this.resolvedItems().find((item) => this.compareWith(item, this.value)) || null);
    }
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
    this.onTouched();
  }

  onSearch(term: { term: string } | string): void {
    const q = typeof term === 'string' ? term : term?.term || '';
    this.search$.next(q);
  }

  onScrollToEnd(): void {
    this.scrollToEnd.emit();
    if (this.isRemote && this.remoteHasMore && !this.loading()) {
      this.fetchRemote(this.remoteTerm, this.remotePage + 1);
    }
  }

  /** Closing the dropdown clears the typed term; the list goes back to its first page. */
  onCleared(): void {
    if (this.isRemote && this.remoteTerm) {
      this.fetchRemote('', 1);
    }
  }

  onOpen(): void {
    this.opened = true;
  }

  onClose(): void {
    this.opened = false;
    this.onCleared();
  }

  onFocus(): void {
    this.focused = true;
  }

  onBlur(): void {
    this.focused = false;
    this.onTouched();
  }

  compareWith = (item: SbSelectItem, selected: unknown): boolean => {
    if (item == null || selected == null) {
      return false;
    }
    if (typeof selected === 'object') {
      return String(this.extractValue(item)) === String(this.extractValue(selected));
    }
    return String(this.extractValue(item)) === String(selected);
  };

  private extractValue(item: unknown): unknown {
    if (item == null || typeof item !== 'object') {
      return item;
    }
    return (item as SbSelectItem)[this.itemValueKey];
  }

  protected resolveItems(): void {
    const mode = this.mode();
    if (mode === 'timezone') {
      this.resolvedItems.set(buildTimezoneOptions(typeof this.value === 'string' ? this.value : null)
        .map((option) => ({ id: option.nameValue, name: option.name })));
      return;
    }
    if (mode === 'language') {
      this.resolvedItems.set([...LANGUAGE_OPTIONS]);
      return;
    }
    if (this.isRemote) {
      // The fixed first page, fetched once; `items` plays no part.
      if (this.remotePage === 0) {
        this.fetchRemote('', 1);
      }
      return;
    }
    this.resolvedItems.set(this.items() || []);
  }

  /**
   * A value that is not on the loaded page would otherwise render as nothing - ng-select
   * only knows the rows in `items`. Put the row it stands for at the top of the list.
   */
  private keepSelectedVisible(): void {
    if (!this.isRemote || this.multiple()) {
      return;
    }
    const chosen = this.selectedItem();
    if (!chosen || this.value == null || this.value === '' || !this.compareWith(chosen, this.value)) {
      return;
    }
    if (!this.resolvedItems().some((item) => this.compareWith(item, this.value))) {
      this.resolvedItems.update((items) => [chosen, ...items]);
    }
  }

  private applyDefault(): void {
    if (this.value != null && this.value !== '') {
      this.defaultApplied = true;
      return;
    }
    const defaultValue = this.defaultValue();
    if (defaultValue == null) {
      return;
    }
    this.value = defaultValue;
    this.onChange(this.value);
    this.defaultApplied = true;
  }

  /**
   * One page from the endpoint. Page 1 replaces the list (a new term, a reset); any later
   * page is appended (the user scrolled). A response that arrives after a newer request
   * was sent is dropped, so fast typing cannot leave the list on a stale term.
   */
  private fetchRemote(term: string, page: number): void {
    const request = ++this.remoteRequest;
    let params = new HttpParams()
      .set(this.pageParam(), String(page))
      .set(this.pageSizeParam(), String(this.pageSize()));
    if (term) {
      params = params.set(this.searchParam(), term);
    }
    for (const [key, raw] of Object.entries(this.searchParams() || {})) {
      if (raw !== undefined && raw !== null && raw !== '') {
        params = params.set(key, String(raw));
      }
    }
    this.loading.set(true);
    this.http.get<unknown>(this.searchEndpoint(), { params }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (request !== this.remoteRequest) {
          return;
        }
        this.loading.set(false);
        const { list, hasMore } = this.readRemotePage(response, page);
        this.remoteTerm = term;
        this.remotePage = page;
        this.remoteHasMore = hasMore;
        this.resolvedItems.update((items) => (page > 1 ? [...items, ...list] : list));
        this.keepSelectedVisible();
      },
      error: () => {
        if (request === this.remoteRequest) {
          this.loading.set(false);
        }
      },
    });
  }

  /** `{data: {list, paging}}` (the API's paged shape), `{data: [...]}`, `{results}` or a bare array. */
  private readRemotePage(response: unknown, page: number): { list: SbSelectItem[]; hasMore: boolean } {
    if (Array.isArray(response)) {
      return { list: response as SbSelectItem[], hasMore: false };
    }
    const body = (response || {}) as { data?: unknown; results?: unknown };
    const data = body.data ?? body.results;
    if (Array.isArray(data)) {
      return { list: data as SbSelectItem[], hasMore: false };
    }
    const paged = (data || {}) as { list?: SbSelectItem[]; paging?: { pages?: number; page?: number } };
    const list = Array.isArray(paged.list) ? paged.list : [];
    const pages = Number(paged.paging?.pages);
    // Without a page count, assume a full page means there may be another.
    const hasMore = Number.isFinite(pages) ? page < pages : list.length >= this.pageSize();
    return { list, hasMore };
  }
}
